import React, { useCallback, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useFormik } from 'formik'

import EditForm from '../../../../../../features/components/Forms/EditForm'
import MultiSelectBox from '../../../../../../features/components/Form/MultiSelectBox'
import ErrorMessage from '../../../../../../components/Form/ErrorMessage'

import { usePurifiedFormik } from '../../../../../../hooks/formHooks/usePurifiedFormik'
import {
  getDeletedCriterionsArray,
  removeExistingCriterions
} from '../../../../../../features/helpers/googleCriterionsHelpers'

import { getGoogleGeolocations } from '../../../../../../modules/actions/googleConstants'
import { clearCreateCampaignCriterions, createCampaignCriterions } from '../../../../../../modules/actions/campaigns'
import {
  campaignCriterionsSelector,
  createCampaignCriterionsErrorSelector,
  createCampaignCriterionsIsLoadingSelector,
  createCampaignCriterionsWasCreatedSelector
} from '../../../../../../modules/selectors/campaigns'
import {
  googleGeolocationsIsLoadingSelector,
  googleGeolocationsSelector
} from '../../../../../../modules/selectors/googleConstants'
import { selectedGoogleAdAccountSelector } from '../../../../../../modules/selectors/adAccounts'

import { CAMPAIGN_GOOGLE_LOCATION_CRITERION_TYPE } from '../../../../../../constants/campaigns'
import { HISTORY_META_EDIT_SECTION, TYPE_CAMPAIGN } from '../../../../../../constants/changeHistory'
import { LOCATIONS } from '../../../../../Multiplatform/CampaignForms/fields'

const GeoTargetingSectionForm = ({ editItemData: campaign, title, ...formProps }) => {
  const dispatch = useDispatch()

  const [manualSuccessSubmit, setManualSuccessSubmit] = useState(false)

  const campaignCriterions = useSelector(campaignCriterionsSelector)
  const criterionsWasCreated = useSelector(createCampaignCriterionsWasCreatedSelector)
  const geolocations = useSelector(googleGeolocationsSelector)
  const geolocationsIsLoading = useSelector(googleGeolocationsIsLoadingSelector)
  const { id: selectedAdAccountId } = useSelector(selectedGoogleAdAccountSelector)

  const { id: campaignId } = campaign

  const formattedInitialGeoLocations = useMemo(
    () =>
      campaignCriterions
        .filter(criterion => criterion.type_ === CAMPAIGN_GOOGLE_LOCATION_CRITERION_TYPE)
        .map(locationCriterion => ({
          label: locationCriterion['display_name'],
          value: locationCriterion['criterion_id'],
          criterionResourceName: locationCriterion.resource_name
        })),
    [campaignCriterions]
  )

  const formattedGeolocations = useMemo(
    () =>
      geolocations &&
      geolocations.map(item => {
        const { id, canonical_name: canonicalLabel, resource_name: resourceName } = item

        return { value: id, label: canonicalLabel, resourceName }
      }),
    [geolocations]
  )

  const onSubmit = useCallback(
    values => {
      const criterionsToDelete = getDeletedCriterionsArray({
        initialData: formattedInitialGeoLocations,
        finalData: values[LOCATIONS]
      })
      const criterionsToCreate = removeExistingCriterions({
        initialData: formattedInitialGeoLocations,
        finalData: values[LOCATIONS]
      })

      if (!criterionsToDelete.length && !criterionsToCreate.length) {
        setManualSuccessSubmit(true)
      } else {
        const formattedCriterionsData = {
          account: selectedAdAccountId,
          operations: criterionsToCreate.map(location => ({
            campaign: campaignId,
            location: {
              geo_target_constant: location.resourceName
            }
          })),
          remove_operations: criterionsToDelete,
          meta: {
            [HISTORY_META_EDIT_SECTION]: title,
            override: {
              item_id: campaignId,
              item_type: TYPE_CAMPAIGN,
              action: 'update'
            }
          }
        }

        dispatch(createCampaignCriterions(formattedCriterionsData, null, criterionsToDelete))
      }
    },
    [dispatch, campaignId, formattedInitialGeoLocations, selectedAdAccountId, title]
  )

  const initialValues = useMemo(
    () => ({
      [LOCATIONS]: formattedInitialGeoLocations || []
    }),
    [formattedInitialGeoLocations]
  )

  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    onSubmit
  })

  const purifiedFormik = usePurifiedFormik(formik)

  const { setFieldValue, values, errors } = purifiedFormik

  const locationsError = errors[LOCATIONS]

  const extraSearchParams = useMemo(() => ({ account: selectedAdAccountId }), [selectedAdAccountId])

  const handleClearCreateCriterions = useCallback(() => {
    dispatch(clearCreateCampaignCriterions())
  }, [dispatch])

  return (
    <EditForm
      formik={purifiedFormik}
      initialValues={initialValues}
      clearEditItem={handleClearCreateCriterions}
      successSubmit={criterionsWasCreated || manualSuccessSubmit}
      errorSelector={createCampaignCriterionsErrorSelector}
      isLoadingSelector={createCampaignCriterionsIsLoadingSelector}
      {...formProps}
    >
      <MultiSelectBox
        title="Add a country or region"
        name={LOCATIONS}
        setFieldValue={setFieldValue}
        options={formattedGeolocations}
        value={values[LOCATIONS]}
        isLoading={geolocationsIsLoading}
        searchAction={getGoogleGeolocations}
        extraSearchParams={extraSearchParams}
      />
      {locationsError && <ErrorMessage error={locationsError} />}
    </EditForm>
  )
}

export default GeoTargetingSectionForm
